import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { Button, MenuItem, TextField } from '@mui/material';
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import AddCandidate from './AddCandidate';
import AddInterview from './AddInterview';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import NoData from '../../loaders/NoData';
import CandidateCard from './CandidateCard';
import PipelineBoard from './PipelineBoard';
import { nextRoundNumber } from './pipeline';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import '../../assets/scss/tables.scss';

function TabPanel(props) {

    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 2 }}>
                    <div>{children}</div>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

/* the tab label carries how many candidates are in that stage */
const tabLabel = (text, count) => (
    <span className="wh-tab-label">{text}<em>{count}</em></span>
);

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const BasicTabs = () => {

    const [value, setValue] = React.useState(0);

    const [pool, setPool] = useState([])

    const [addCandidateModal, setAddCandidateModal] = useState(false)

    const [scheduledData, setScheduledData] = useState([]);

    const [selectedCandidates, setSelectedCandidates] = useState([]);

    const [rejectedCandidates, setRejectedCandidates] = useState([]);

    const [selectedCandidateForEdit, setSelectedCandidateForEdit] = useState({});

    const [isLoad, setIsLoad] = useState(false);

    const [jobJDS, setjobJDS] = useState([]);

    const [jobId, setJobId] = useState('All')

    /* the candidate whose next round is being booked */
    const [scheduleFor, setScheduleFor] = useState(null)

    /* The four endpoints return four disjoint slices of the same collection,
       so the one view that shows every stage at once is their union. Pool is
       fetched unfiltered, so the job filter is applied here for all of them
       rather than only for the three that ask the server. */
    const everyone = React.useMemo(() => {
        const seen = new Set();
        return [...pool, ...scheduledData, ...selectedCandidates, ...rejectedCandidates]
            .filter(candidate => {
                if (seen.has(candidate.candidateID)) return false;
                seen.add(candidate.candidateID);
                return jobId === 'All' || candidate.jobID === jobId;
            });
    }, [pool, scheduledData, selectedCandidates, rejectedCandidates, jobId]);

    const editCandidate = (candidate) => {
        setSelectedCandidateForEdit(candidate);
        setAddCandidateModal(true);
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };
    const { fetchData } = useAxios();

    useEffect(() => {
        getAllCandidates();
        GetJobJds();

    }, [])

    useEffect(() => {
        getScheduledCandidates();
        getSelectedCandidates();
        getRejectedCandidates();
    }, [jobId])

    const GetJobJds = async () => {
        const { data } = await fetchData({ url: URLS.getJobs, method: 'get' })
        setjobJDS(data || []);
    }

    const getAllCandidates = async () => {
        setIsLoad(true);
        let response = await fetchData({ url: URLS.getAllCandidates, method: 'get' })
        if (response.status === 200) {
            setPool(response.data);
            setIsLoad(false)
        }
        else {
            toast.error(response.message);
        }
    }

    const getScheduledCandidates = async () => {
        setIsLoad(true);
        let response = jobId === 'All' ?
            await fetchData({ url: URLS.getScheduledCandidates, method: 'get' }) :
            await fetchData({ url: URLS.getScheduledCandidates + '?jobID=' + jobId, method: 'get' })
        if (response.status === 200) {
            setScheduledData(response.data);
            setIsLoad(false);
        }
        else {
            toast.error(response.message);
        }
    }

    const getSelectedCandidates = async () => {
        setIsLoad(true);
        let response = jobId === 'All' ?
            await fetchData({ url: URLS.getSelectedCandidates, method: 'get' }) :
            await fetchData({ url: URLS.getSelectedCandidates + '?jobID=' + jobId, method: 'get' })
        if (response.status === 200) {
            setSelectedCandidates(response.data);
            setIsLoad(false)
        }
        else {
            toast.error(response.message);
        }
    }

    const getRejectedCandidates = async () => {
        setIsLoad(true);
        let response = jobId === 'All' ?
            await fetchData({ url: URLS.getRejectedCandidates, method: 'get' }) :
            await fetchData({ url: URLS.getRejectedCandidates + '?jobID=' + jobId, method: 'get' })
        if (response.status === 200) {
            setRejectedCandidates(response.data);
            setIsLoad(false)
        }
        else {
            toast.error(response.message);
        }
    }

    const reloadEveryStage = () => {
        getAllCandidates();
        getScheduledCandidates();
        getSelectedCandidates();
        getRejectedCandidates();
    }

    return (
        <div className='wh-table'>
            <Box sx={{ width: '100%' }} position={'relative'}>

                <div className='wh-toolbar wh-recruiter-bar'>
                    <Tabs value={value} onChange={handleChange} aria-label="candidate pipeline"
                        variant="scrollable" scrollButtons="auto" className='wh-tabs'>
                        <Tab label={tabLabel('Pipeline', everyone.length)} disableRipple
                            icon={<DashboardOutlinedIcon />} iconPosition="start"
                            {...a11yProps(0)} onClick={reloadEveryStage} />
                        <Tab label={tabLabel('Pool', pool.length)} disableRipple
                            icon={<GroupsOutlinedIcon />} iconPosition="start"
                            {...a11yProps(1)} onClick={getAllCandidates} />
                        <Tab label={tabLabel('Scheduled', scheduledData.length)} disableRipple
                            icon={<EventAvailableOutlinedIcon />} iconPosition="start"
                            {...a11yProps(2)} onClick={getScheduledCandidates} />
                        <Tab label={tabLabel('Selected', selectedCandidates.length)} disableRipple
                            icon={<HowToRegOutlinedIcon />} iconPosition="start"
                            {...a11yProps(3)} onClick={getSelectedCandidates} />
                        <Tab label={tabLabel('Rejected', rejectedCandidates.length)} disableRipple
                            icon={<PersonOffOutlinedIcon />} iconPosition="start"
                            {...a11yProps(4)} onClick={getRejectedCandidates} />
                    </Tabs>

                    <div className='wh-recruiter-controls'>
                        <TextField select label='Job' size='small'
                            value={jobId} onChange={(e) => setJobId(e.target.value)}>
                            <MenuItem value='All'>All jobs</MenuItem>
                            {jobJDS.map((job, i) =>
                                <MenuItem value={job.jobID} key={i}>{job.jobTitle}</MenuItem>
                            )}
                        </TextField>
                        <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />}
                            onClick={() => { setAddCandidateModal(true); setSelectedCandidateForEdit({}) }}>
                            Add Candidate
                        </Button>
                    </div>
                </div>

                {/* every stage at once — the tiles and the table both read
                    from the same union, so they can never disagree */}
                <TabPanel value={value} index={0}>
                    <PipelineBoard candidates={everyone} isLoad={isLoad}
                        onEdit={editCandidate}
                        onSchedule={(candidate) => setScheduleFor(candidate)} />
                </TabPanel>
                <TabPanel value={value} index={1} >
                    {pool.length > 0
                        ? <CandidateCard pool={pool} showEditBtn accent="wh-accent-amber"
                            onEdit={editCandidate}
                            onSchedule={(candidate) => setScheduleFor(candidate)} />
                        : (!isLoad ? <NoData /> : "")}
                </TabPanel>
                <TabPanel value={value} index={2}>
                    {scheduledData.length > 0
                        ? <CandidateCard pool={scheduledData} showInterview accent="wh-accent-blue"
                            onSchedule={(candidate) => setScheduleFor(candidate)} />
                        : (!isLoad ? <NoData /> : "")}
                </TabPanel>
                <TabPanel value={value} index={3}>
                    {selectedCandidates.length > 0
                        ? <CandidateCard pool={selectedCandidates} showOutcome accent="wh-accent-green" />
                        : (!isLoad ? <NoData /> : "")}
                </TabPanel>
                <TabPanel value={value} index={4}>
                    {rejectedCandidates.length > 0
                        ? <CandidateCard pool={rejectedCandidates} showOutcome accent="wh-accent-red" />
                        : (!isLoad ? <NoData /> : "")}
                </TabPanel>

                <AddCandidate show={addCandidateModal} onHide={setAddCandidateModal}
                    reloadContent={getAllCandidates} selectedCandidateForEdit={selectedCandidateForEdit} />

                {/* Booking from the pool is the first round; booking from a
                    candidate already part-way through is the round after the
                    last one on record. */}
                <Dialog open={!!scheduleFor} onClose={() => setScheduleFor(null)}
                    maxWidth="md" fullWidth className="wh-dialog">
                    <DialogTitle>
                        Schedule an interview
                        <span className="wh-dialog-sub">
                            {scheduleFor?.name} · {scheduleFor?.candidateID}
                            {scheduleFor ? ` · Round ${nextRoundNumber(scheduleFor)}` : ''}
                        </span>
                    </DialogTitle>
                    <DialogContent dividers>
                        {scheduleFor && (
                            <AddInterview
                                selectedRoundData={{
                                    candidateID: scheduleFor.candidateID,
                                    round: nextRoundNumber(scheduleFor)
                                }}
                                setEditInterview={() => setScheduleFor(null)}
                                reloadCandidate={() => {
                                    setScheduleFor(null);
                                    reloadEveryStage();
                                }} />
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </div>
    );
}

export default BasicTabs